// src/app/components/onboarding/stage3-battery.tsx
/**
 * The question battery on the Stage 3 screen (#137): the linter's rules, the
 * personas-by-forms and beliefs-by-forms matrix with the failing cells, and
 * each question's category-context verdict.
 *
 * The server is the authority (backend/app/services/question_battery/rules.py):
 * the screen lints the battery being edited through POST .../battery/lint and
 * the approval refuses on the same result. Nothing here re-implements a rule.
 */
import React, { useState } from "react";
import { AlertTriangle, Ban, Check, Loader2, RefreshCw } from "lucide-react";
import type {
  BatteryBlock,
  BatteryLint,
  BatteryLintRule,
  BatteryMatrixCell,
  LintStatus,
} from "@/api/onboarding-api";

const STATUS_COLOR: Record<LintStatus, string> = {
  red: "var(--infrared-coral)",
  yellow: "var(--signal-amber, #D9A400)",
  green: "var(--chart-3, var(--alpha-teal))",
};

const small: React.CSSProperties = { fontSize: "var(--text-xs)", color: "var(--muted-foreground)" };

function Dot({ status }: { status: LintStatus }) {
  return <span className="inline-block rounded-full" style={{ width: 8, height: 8, backgroundColor: STATUS_COLOR[status], flexShrink: 0 }} />;
}

/** The rules, worst first, each with its findings; a yellow rule can be
 *  acknowledged with a reason (recorded with who and when on save). */
export function BatteryLintPanel({
  lint,
  battery,
  onBatteryChange,
  locked,
  onSelectQuestions,
}: {
  lint: BatteryLint;
  battery: BatteryBlock;
  onBatteryChange: (b: BatteryBlock) => void;
  locked: boolean;
  onSelectQuestions: (ids: string[]) => void;
}) {
  const order: Record<LintStatus, number> = { red: 0, yellow: 1, green: 2 };
  const rules = [...lint.rules].sort((a, b) => order[a.status] - order[b.status]);
  const red = lint.rules.filter((r) => r.status === "red").length;
  const yellow = lint.rules.filter((r) => r.status === "yellow" && !r.acknowledged).length;
  return (
    <div className="rounded-lg p-3" style={{ border: "1px solid var(--border)" }}>
      <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
        <Dot status={lint.status} />
        Battery linter
        <span style={{ ...small, fontWeight: 400 }}>
          {red === 0 && yellow === 0
            ? "every rule passes"
            : `${red} red${yellow ? `, ${yellow} yellow to acknowledge` : ""}`}
          {!lint.gated && " · this battery predates the linter, so it does not block approval"}
          {lint.gated && lint.blocking && " · approval is blocked"}
        </span>
      </div>
      <div className="mt-2 space-y-1.5">
        {rules.map((rule) => (
          <RuleRow key={rule.rule} rule={rule} battery={battery} onBatteryChange={onBatteryChange}
            locked={locked} onSelectQuestions={onSelectQuestions} />
        ))}
      </div>
    </div>
  );
}

function RuleRow({
  rule, battery, onBatteryChange, locked, onSelectQuestions,
}: {
  rule: BatteryLintRule;
  battery: BatteryBlock;
  onBatteryChange: (b: BatteryBlock) => void;
  locked: boolean;
  onSelectQuestions: (ids: string[]) => void;
}) {
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(rule.status === "red");
  const shown = open ? rule.findings : [];
  const acknowledge = () => {
    const text = reason.trim();
    if (!text) return;
    const others = (battery.acknowledgements || []).filter((a) => a.rule !== rule.rule);
    onBatteryChange({ ...battery, acknowledgements: [...others, { rule: rule.rule, fingerprint: rule.fingerprint, reason: text }] });
    setReason("");
  };
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 w-full text-left"
        style={{ background: "none", border: "none", cursor: rule.findings.length ? "pointer" : "default", padding: 0 }}
      >
        <Dot status={rule.status === "yellow" && rule.acknowledged ? "green" : rule.status} />
        <span style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--foreground)" }}>{rule.label}</span>
        {rule.findings.length > 0 && <span style={small}>({rule.findings.length})</span>}
        {rule.acknowledged && (
          <span style={small}>· acknowledged{rule.acknowledged.by ? ` by ${rule.acknowledged.by}` : ""}: {rule.acknowledged.reason}</span>
        )}
      </button>
      {shown.length > 0 && (
        <ul style={{ margin: "4px 0 4px 18px", listStyle: "disc" }}>
          {shown.map((f, i) => (
            <li key={i} style={{ fontSize: 11, color: rule.status === "red" ? "var(--infrared-coral)" : "var(--foreground)" }}>
              {f.message}
              {f.query_ids.length > 0 && (
                <button onClick={() => onSelectQuestions(f.query_ids)}
                  style={{ marginLeft: 6, background: "none", border: "none", padding: 0, cursor: "pointer",
                    color: "var(--signal-blue)", fontSize: 11, textDecoration: "underline" }}>
                  show
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && rule.ackable && !rule.acknowledged && !locked && (
        <div className="flex items-center gap-2 mt-1" style={{ marginLeft: 18 }}>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this is acceptable (recorded)"
            className="px-2 py-1 rounded-md flex-1"
            style={{ fontSize: "var(--text-xs)", border: "1px solid var(--border)", background: "var(--background)", color: "var(--foreground)" }} />
          <button onClick={acknowledge} disabled={!reason.trim()}
            className="px-2 py-1 rounded-md"
            style={{ fontSize: "var(--text-xs)", border: "1px solid var(--border)", background: "var(--muted)",
              color: "var(--foreground)", cursor: reason.trim() ? "pointer" : "not-allowed", opacity: reason.trim() ? 1 : 0.5 }}>
            Acknowledge
          </button>
        </div>
      )}
    </div>
  );
}

function cellStyle(cell: BatteryMatrixCell | undefined, selected: boolean): React.CSSProperties {
  const failing = !!cell?.failing.length;
  return {
    border: `1px solid ${selected ? "var(--alpha-teal)" : failing ? "var(--infrared-coral)" : "var(--border)"}`,
    backgroundColor: cell?.excluded
      ? "var(--muted)"
      : failing ? "color-mix(in srgb, var(--infrared-coral) 10%, transparent)" : "transparent",
    padding: "4px 6px",
    textAlign: "center",
    minWidth: 76,
  };
}

/** Personas by forms (with excluded cells and "regenerate this cell"), then
 *  beliefs by forms. Clicking a count filters the question list to it. */
export function BatteryMatrixView({
  lint,
  battery,
  onBatteryChange,
  onSelectCell,
  selectedCell,
  onRegenerateCell,
  regenerating,
  locked,
}: {
  lint: BatteryLint;
  battery: BatteryBlock;
  onBatteryChange: (b: BatteryBlock) => void;
  onSelectCell: (cell: { personaId?: string; beliefId?: string; form: string } | null) => void;
  selectedCell: { personaId?: string; beliefId?: string; form: string } | null;
  onRegenerateCell: (personaId: string, form: string) => void;
  regenerating: string | null;
  locked: boolean;
}) {
  const m = lint.matrix;
  const toggleExcluded = (personaId: string, form: string, excluded: boolean) => {
    const rest = (battery.excluded_cells || []).filter((c) => !(c.persona_id === personaId && c.form === form));
    onBatteryChange({ ...battery, excluded_cells: excluded ? rest : [...rest, { persona_id: personaId, form }] });
  };
  const th: React.CSSProperties = { ...small, fontWeight: 600, padding: "4px 6px", textAlign: "center" };
  const rowHead = (label: string, id: string, total: number, failing: string[]) => (
    <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>
      <span style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: failing.length ? "var(--infrared-coral)" : "var(--foreground)" }}>
        {failing.length > 0 && <AlertTriangle className="w-3 h-3 inline mr-1" />}
        {label || id}
      </span>
      <span style={small}> · {id} · {total}</span>
    </td>
  );
  return (
    <div className="space-y-3">
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 3 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>Persona × form</th>
              {m.forms.map((f) => <th key={f.id} style={th}>{f.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {m.personas.map((p) => (
              <tr key={p.id}>
                {rowHead(p.label, p.id, m.persona_totals[p.id] ?? 0, m.persona_failing[p.id] ?? [])}
                {m.forms.map((f) => {
                  const cell = m.persona_cells[p.id]?.[f.id];
                  const key = `${p.id}:${f.id}`;
                  const selected = selectedCell?.personaId === p.id && selectedCell.form === f.id;
                  return (
                    <td key={f.id} style={cellStyle(cell, selected)}>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onSelectCell(selected ? null : { personaId: p.id, form: f.id })}
                          title={cell?.excluded ? "Marked: this role does not have this conversation" : "Show these questions"}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                            fontSize: "var(--text-sm)", fontWeight: 600,
                            color: cell?.excluded ? "var(--muted-foreground)" : "var(--foreground)" }}
                        >
                          {cell?.excluded && !cell.count ? "—" : cell?.count ?? 0}
                        </button>
                        {!locked && (
                          <>
                            <button
                              onClick={() => toggleExcluded(p.id, f.id, !!cell?.excluded)}
                              title={cell?.excluded ? "Clear the mark" : "Mark: this role does not have this conversation"}
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                                color: cell?.excluded ? "var(--infrared-coral)" : "var(--muted-foreground)" }}
                            >
                              <Ban className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => onRegenerateCell(p.id, f.id)}
                              disabled={!!regenerating || !!cell?.excluded}
                              title="Regenerate this cell (the rest of the battery stays as it is)"
                              style={{ background: "none", border: "none", padding: 0,
                                cursor: regenerating || cell?.excluded ? "not-allowed" : "pointer",
                                color: "var(--muted-foreground)", opacity: cell?.excluded ? 0.4 : 1 }}
                            >
                              {regenerating === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td style={{ ...small, padding: "4px 6px" }}>Total / target</td>
              {m.forms.map((f) => {
                const have = m.form_totals[f.id] ?? 0;
                const want = m.targets[f.id] ?? 0;
                return (
                  <td key={f.id} style={{ ...small, textAlign: "center", fontWeight: 600,
                    color: have === want ? "var(--muted-foreground)" : "var(--infrared-coral)" }}>
                    {have} / {want}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 3 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>Belief × form</th>
              {m.forms.map((f) => <th key={f.id} style={th}>{f.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {m.beliefs.map((b) => (
              <tr key={b.id}>
                {rowHead(b.label.length > 48 ? `${b.label.slice(0, 47)}…` : b.label, b.id,
                  m.belief_totals[b.id] ?? 0, m.belief_failing[b.id] ?? [])}
                {m.forms.map((f) => {
                  const cell = m.belief_cells[b.id]?.[f.id];
                  const selected = selectedCell?.beliefId === b.id && selectedCell.form === f.id;
                  return (
                    <td key={f.id} style={cellStyle(cell, selected)}>
                      <button
                        onClick={() => onSelectCell(selected ? null : { beliefId: b.id, form: f.id })}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                          fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}
                      >
                        {cell?.count ?? 0}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {m.unplaced.length > 0 && (
        <p style={{ ...small, color: "var(--infrared-coral)" }}>
          Not on the matrix (no valid form or persona): {m.unplaced.join(", ")}
        </p>
      )}
    </div>
  );
}

/** The question's category-context verdict: the words that carry it
 *  highlighted, or the question marked when it has none. */
export function QuestionContext({
  text,
  verdict,
}: {
  text: string;
  verdict?: { status: "present" | "missing" | "unchecked"; context?: string; reason?: string };
}) {
  if (!verdict || !text.trim()) return null;
  if (verdict.status === "unchecked") {
    return <div style={{ ...small, marginTop: 4 }}>Category context: not checked since this question changed; saving checks it.</div>;
  }
  if (verdict.status === "missing") {
    return (
      <div style={{ fontSize: "var(--text-xs)", marginTop: 4, padding: "2px 6px", borderRadius: 4,
        color: "var(--infrared-coral)", backgroundColor: "color-mix(in srgb, var(--infrared-coral) 12%, transparent)" }}>
        <AlertTriangle className="w-3 h-3 inline mr-1" />
        No category context: “{text}”{verdict.reason ? ` · ${verdict.reason}` : ""}
      </div>
    );
  }
  const phrase = verdict.context || "";
  const at = phrase ? text.toLowerCase().indexOf(phrase.toLowerCase()) : -1;
  return (
    <div style={{ ...small, marginTop: 4 }}>
      <Check className="w-3 h-3 inline mr-1" style={{ color: STATUS_COLOR.green }} />
      Category context:{" "}
      {at < 0 ? phrase : (
        <>
          {text.slice(0, at)}
          <mark style={{ backgroundColor: "color-mix(in srgb, var(--alpha-teal) 25%, transparent)", color: "var(--foreground)", padding: "0 2px", borderRadius: 2 }}>
            {text.slice(at, at + phrase.length)}
          </mark>
          {text.slice(at + phrase.length)}
        </>
      )}
    </div>
  );
}
